import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Search, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const getDomain = (email) => {
  const parts = (email || '').split('@');
  return parts.length > 1 ? parts[1].toLowerCase() : 'unknown';
};

const TeamProjects = () => {
  const { user } = useOutletContext();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDomain, setActiveDomain] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/team-projects');
        setData(res.data);
      } catch (e) { toast.error('Failed to load'); }
      setLoading(false);
    };
    load();
  }, []);

  // Extract unique domains
  const domains = useMemo(() => {
    const domainMap = {};
    for (const d of data) {
      const domain = getDomain(d.resource);
      if (!domainMap[domain]) domainMap[domain] = { domain, count: 0 };
      domainMap[domain].count++;
    }
    return Object.values(domainMap).sort((a, b) => b.count - a.count);
  }, [data]);

  const filtered = useMemo(() => {
    let list = data;
    if (activeDomain !== 'all') {
      list = list.filter(d => getDomain(d.resource) === activeDomain);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d => d.resource.toLowerCase().includes(q));
    }
    return list;
  }, [data, activeDomain, search]);

  const totals = useMemo(() => ({
    members: filtered.length,
    projects: filtered.reduce((s, d) => s + d.total_projects, 0),
    completed: filtered.reduce((s, d) => s + d.completed, 0),
    inProgress: filtered.reduce((s, d) => s + d.in_progress, 0),
    minutes: Math.round(filtered.reduce((s, d) => s + d.total_minutes, 0)),
  }), [filtered]);

  if (loading) {
    return <DashboardLayout user={user}><div className="flex items-center justify-center h-96"><div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="team-projects-title">Team Projects</h1>
          <p className="text-muted-foreground">Per-resource email project stats — {data.length} team members</p>
        </div>

        {/* Domain Tabs */}
        <div className="flex gap-1 flex-wrap bg-muted rounded-lg p-1">
          <Button variant={activeDomain === 'all' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveDomain('all')} data-testid="domain-all">
            All ({data.length})
          </Button>
          {domains.map(d => (
            <Button
              key={d.domain}
              variant={activeDomain === d.domain ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveDomain(d.domain)}
              data-testid={`domain-${d.domain}`}
            >
              @{d.domain} ({d.count})
            </Button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-violet-600">{totals.members}</p>
              <p className="text-xs text-muted-foreground">Members</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{totals.projects}</p>
              <p className="text-xs text-muted-foreground">Projects</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-green-600">{totals.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-orange-600">{totals.inProgress}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{totals.minutes.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Minutes</p>
            </CardContent>
          </Card>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email..." className="pl-10" />
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead className="text-center">Resource Email</TableHead>
                    <TableHead className="text-center">Domain</TableHead>
                    <TableHead className="text-center">Projects</TableHead>
                    <TableHead className="text-center">Completed</TableHead>
                    <TableHead className="text-center">In Progress</TableHead>
                    <TableHead className="text-center">Total Minutes</TableHead>
                    <TableHead className="text-center">Unique Titles</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((d, i) => (
                    <TableRow key={d.resource}>
                      <TableCell className="text-center text-muted-foreground text-xs">{i + 1}</TableCell>
                      <TableCell className="text-center text-sm font-medium">{d.resource}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-[10px]">@{getDomain(d.resource)}</Badge>
                      </TableCell>
                      <TableCell className="text-center font-semibold">{d.total_projects}</TableCell>
                      <TableCell className="text-center text-green-600">{d.completed}</TableCell>
                      <TableCell className="text-center text-blue-600">{d.in_progress}</TableCell>
                      <TableCell className="text-center">{d.total_minutes.toLocaleString()}</TableCell>
                      <TableCell className="text-center">{d.unique_titles}</TableCell>
                      <TableCell className="text-center">
                        {d.in_progress > 0
                          ? <Badge className="bg-green-100 text-green-700 text-[10px]">Active</Badge>
                          : <Badge className="bg-gray-100 text-gray-500 text-[10px]">Idle</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TeamProjects;
